import { useContext, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { APP_TITLE } from "../../../config/constant";
import { defaultStatusColors } from "../../../utils/CardUtils";
import InfinityGridInvitationsUsersCards from "../../common/cardContainers/InfinityGridInvitationsUsersCards";
import ListComponent from "../../common/lists/ListComponent";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import AppContext from "../../../AppContext";
import { roleAccesse } from "../../../utils/roleManager";
import * as Library from "../../../utils/Library";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
/* #endregion */

export default function UserInvitationList() {
    document.title = `${APP_TITLE} - Users Invites`;

    // #region STATES
    const { MainLayoutProps, layoutType } = useOutletContext();
    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const { userData } = useContext(AppContext);
    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");

    const navigations = useSelector(state => state.navigationAccesses);

    //---------- Header Filters
    const [searchText, setSearchText] = useState("");
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    const [allRetailersList, setAllRetailersList] = useState([]);
    const [filteredRetailers, setFilteredRetailers] = useState([]);
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [requestEnabled, setRequestsEnabled] = useState(false);
    //-----
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const [listNumRows, setListNumRows] = useState(20);

    const filters = {
        //labelBrandIDs: filteredLabelBrand?.map(item => item?.value),
        //filterRetailBrandIDs: filteredRetailers?.map(item => item?.value)
        //retailBrandIDs: [],
        filters: {
            labelBrandIDs: filteredLabelBrand?.map(item => item?.value)
        },
        searchText: searchText
    };

    const infiniteGridQuerykey = ["RetailersConnectionsGrid", filters];
    const listQueryKey = ["RetailersConnectionsList", filters];
    const gridRef = useRef(null);

    // #endregion

    // #region METHODS
    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
            gridRef.current?.reset?.(infiniteGridQuerykey);
        }
    };

    /* const onLabelBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredLabelBrand(selections);
        setAllLabelBrandsList(dropdownDataWithSelections);
    }; */

    const onRetailersChange = (selections, dropdownDataWithSelections) => {
        setFilteredRetailers(selections);
        setAllRetailersList(dropdownDataWithSelections);
    };

    const onLabelBrandChange = selection => {
        setFilteredLabelBrand([selection]);

        sessionStorage.setItem("currentLabelBrand", selection?.value || "");
    };

    /* const onFiltersLoaded = data => {
        if (data?.labelBrands?.length) {
            const labelBrands = data?.labelBrands?.map(item => {
                return {
                    name: item?.name,
                    value: item?._id,
                    selected: true
                };
            });

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand(labelBrands);
        }

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                return {
                    name: item?.name,
                    value: item?._id,
                    selected: true
                };
            });

            setAllRetailersList(retailBrands);
            setFilteredRetailers(retailBrands?.filter(item => item?.selected));
        }

        setRequestsEnabled(true);
    }; */

    const onFiltersLoaded = data => {
        if (data?.labelBrands?.length) {
            const defaultLabelBrand =
                sessionStorage.getItem("currentLabelBrand") || localStorage.getItem("defaultLabelBrand");
            const labelBrands = data?.labelBrands?.map((item, index) => {
                const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
                let labelBrandName = item?.name;

                if (item?._id === defaultLabelBrandID) {
                    labelBrandName = `${item?.name} (default)`;
                }

                return {
                    name: labelBrandName,
                    value: item?._id,
                    selected: item?._id === defaultLabelBrand
                };
            });

            const defaultLabelBrandObj =
                labelBrands?.find(item => item?.value === defaultLabelBrand) || labelBrands?.[0];

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand([defaultLabelBrandObj]);
        }
        setRequestsEnabled(true);
    };

    const onSearch = searchText => {
        setSearchText(searchText);
    };
    //#endregion METHODS

    /* #region EFFECTS */
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);
        setupHeaders(
            props,
            onClickRefreshAction,
            navigations,
            allRetailersList,
            allLabelBrandsList,
            onLabelBrandChange,
            onRetailersChange,
            onFiltersLoaded,
            requestEnabled,
            filteredLabelBrand,
            filteredRetailers,
            onSearch,
            editAccess
        );
    }, [
        props?.layoutType,
        requestEnabled,
        filteredLabelBrand,
        filteredRetailers,
        allRetailersList,
        allLabelBrandsList,
        editAccess
    ]);
    /* #endregion */

    /* #region LIST COMPONENT PROPS */
    const listColumns = [
        {
            name: "First Name",
            selector: row => row?.contactFirstName ?? "--"
        },
        {
            name: "Last Name",
            selector: row => row?.contactLastName ?? "--"
        },
        {
            name: "Role",
            selector: row => row?.userRole ?? "--"
        },
        {
            name: "Stage Level",
            selector: row => (row?.userAccessLevel === "Organization level" ? "Account level" : row?.userAccessLevel)
        },
        {
            name: "Phone number",
            selector: row => row?.contactMobileNumber ?? "--"
        },
        {
            name: "Email",
            selector: row => row?.contactEmail ?? "--"
        }
    ];
    /* #endregion */

    return (
        <StyledPage>
            {viewLayoutStyle === "Grid" && (
                <InfinityGridInvitationsUsersCards
                    ref={gridRef}
                    minColumnWidth="238px"
                    defaultSecondTitle="Account:"
                    limit={10}
                    queryKey={infiniteGridQuerykey}
                    statusFromPath="status"
                    requestEnabled={requestEnabled}
                    filters={filters}
                    context="Invites"
                    endpoint="getInvitedUsersData"
                />
            )}
            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="INVITED USERS"
                    columns={listColumns}
                    api={{
                        useCommonPostRequest: true,
                        commonContext: "Invites",
                        endpoint: "getInvitedUsersData",
                        formData: {
                            organizationID: localStorage.getItem("currentOrganizationID"),
                            limit: listNumRows,
                            searchText: searchText,
                            ...filters
                        }
                        //onSuccess: mapListData
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{ includeActions: true, extraActions: [{ title: "Details" }] }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "status",
                        statusColors: defaultStatusColors
                    }}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </StyledPage>
    );
}

function setupHeaders(
    props,
    onClickRefreshAction,
    navigations,
    allRetailersList,
    allLabelBrandsList,
    onLabelBrandChange,
    onRetailersChange,
    onFiltersLoaded,
    requestEnabled,
    filteredLabelBrand,
    filteredRetailers,
    onSearch,
    editAccess
) {
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Search" /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/users", active: false, disabled: false, title: "users" },
        { to: "/users/invitations", active: true, disabled: false, title: "INVITES" }
    ]);
    props.setControlBarRightBtns([
        { to: "/users/invitations", active: true, disabled: false, title: "View", icon: "eye" },
        { to: editAccess ? "/users/invite" : "#", active: false, disabled: !editAccess, title: "Invite", icon: "mail" },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                onRefreshClick={onClickRefreshAction}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                firstOrganize={
                    Library.isAdminDebug()
                        ? {
                              visible: true,
                              dropdownData: allLabelBrandsList,
                              onDropdownChange: onLabelBrandChange,
                              disabled: false,
                              type: "singleSelect"
                          }
                        : {
                              visible: true,
                              disabled: true
                          }
                }
                secondOrganize={{
                    visible: true,
                    disabled: true
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useLabelBrands: true,
                        useRetailBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
            />
        )
    });
}
